import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Autocomplete,
  Checkbox,
  ListItemText,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Clear,
  ArrowUpward,
  ArrowDownward,
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
  FileDownload,
} from '@mui/icons-material';
import './GridView.css';

const ALPHABET_OPTIONS = Array.from({ length: 26 }, (_, index) => String.fromCharCode(65 + index));
const BOOLEAN_FILTER_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const multiSelectAutocompleteSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    alignItems: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  '& .MuiOutlinedInput-input': {
    padding: 0,
  },
};

const GridPaginationActions = ({ page, count, rowsPerPage, onPageChange, disabled }) => {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const handleFirstPage = (event) => {
    onPageChange(event, 0);
  };

  const handleBackPage = (event) => {
    onPageChange(event, Math.max(page - 1, 0));
  };

  const handleNextPage = (event) => {
    onPageChange(event, Math.min(page + 1, lastPage));
  };

  const handleLastPage = (event) => {
    onPageChange(event, lastPage);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton onClick={handleFirstPage} disabled={disabled || page === 0} size="small" aria-label="first page">
        <FirstPage />
      </IconButton>
      <IconButton onClick={handleBackPage} disabled={disabled || page === 0} size="small" aria-label="previous page">
        <NavigateBefore />
      </IconButton>
      <IconButton onClick={handleNextPage} disabled={disabled || page >= lastPage} size="small" aria-label="next page">
        <NavigateNext />
      </IconButton>
      <IconButton onClick={handleLastPage} disabled={disabled || page >= lastPage} size="small" aria-label="last page">
        <LastPage />
      </IconButton>
    </Box>
  );
};

const GridView = ({
  locations,
  suppliers,
  filteredDataList,
  handleCardClick,
  selectedSupplier,
  selectedLocation,
  selectedRepairLocation,
  selectedNominalCode,
  handleSupplierChange,
  handleLocationChange,
  handleRepairLocationChange,
  handleNominalCodeChange,
  handleClearFilters,
  supplierStartChar,
  supplierEndChar,
  onSupplierStartCharChange,
  onSupplierEndCharChange,
  sortCriteria,
  setSortCriteria,
  sortOrder,
  setSortOrder,
  currentPage,
  totalPages,
  handlePageChange,
  totalDocuments,
  totalNetValue,
  pageLimit,
  selectedDocStatuses,
  selectedDocTypes,
  handleDocStatusChange,
  handleDocTypeChange,
  docStatuses,
  docTypes,
  nominalCodes = [],
  onSearchDocument,     // NEW: parent callback
  docSearchQuery,
  uploadedStartDate,
  uploadedEndDate,
  onUploadedStartDateChange,
  onUploadedEndDateChange,
  createdStartDate,
  createdEndDate,
  onCreatedStartDateChange,
  onCreatedEndDateChange,
  discountOperator,
  discountValue,
  onDiscountOperatorChange,
  onDiscountValueChange,
  includeInNetTotalsFilter = 'any',
  creditRequestedFilter = 'any',
  onIncludeInNetTotalsFilterChange,
  onCreditRequestedFilterChange,
  invoiceStartDate,
  invoiceEndDate,
  onInvoiceStartDateChange,
  onInvoiceEndDateChange,
  jobNumberFilter,
  onJobNumberSearch,
  onExportCsv,
  exporting = false,
  loading = false,
  defaultSelectedDocStatuses = [],
  defaultSelectedDocTypes = [],
  defaultSortCriteria = '',
  defaultSortOrder = 'asc',
  documentView = null,
}) => {
  const [hoveredHeader, setHoveredHeader] = useState(null);
  const [pageInput, setPageInput] = useState(String(currentPage));
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const [docQuery, setDocQuery] = useState('');  // NEW: local search input
  const [jobQuery, setJobQuery] = useState('');
  useEffect(() => {
    setDocQuery(docSearchQuery || '');
  }, [docSearchQuery]);

  useEffect(() => {
    setJobQuery(jobNumberFilter || '');
  }, [jobNumberFilter]);

  const supplierMap = suppliers.reduce((acc, s) => ({ ...acc, [s.supplier_id]: s.name }), {});
  const selectedSupplierOption = useMemo(
    () => (selectedSupplier ? suppliers.find((s) => s.supplier_id === selectedSupplier) : null),
    [selectedSupplier, suppliers],
  );
  const locationMap = locations.reduce((acc, l) => ({ ...acc, [l.location_id]: l.name }), {});
  const statusMap = docStatuses.reduce((acc, st) => ({ ...acc, [st.doc_status_id]: st.name }), {});
  const typeMap = docTypes.reduce((acc, t) => ({ ...acc, [t.doc_type_id]: t.name }), {});
  const nominalCodeOptions = Array.isArray(nominalCodes) ? nominalCodes : [];
  const formatCellDisplay = useCallback((value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : '-';
    }
    return value;
  }, []);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const formatPercent = useCallback((value) => {
    if (value === null || value === undefined) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return formatCellDisplay(value);
    return `${num.toFixed(2)}%`;
  }, [formatCellDisplay]);

  const parseDateForSort = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }
    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts.map((part) => Number(part));
        if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
          const parsed = new Date(year, month - 1, day);
          if (!Number.isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }
      const fallback = new Date(value);
      if (!Number.isNaN(fallback.getTime())) {
        return fallback;
      }
    }
    return null;
  };

  const handleFirstPageButtonClick = useCallback(() => { handlePageChange(1); }, [handlePageChange]);
  const handleBackButtonClick = useCallback(() => { if (currentPage > 1) handlePageChange(Math.max(currentPage - 1, 1)); }, [currentPage, handlePageChange]);
  const handleNextButtonClick = useCallback(() => { if (currentPage < totalPages) handlePageChange(Math.min(currentPage + 1, totalPages)); }, [currentPage, handlePageChange, totalPages]);
  const handleLastPageButtonClick = useCallback(() => { handlePageChange(totalPages); }, [handlePageChange, totalPages]);

  const startDocNum = (currentPage - 1) * pageLimit + 1;
  const endDocNum = Math.min(currentPage * pageLimit, totalDocuments);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    if (!loading) {
      setHasFetchedData(true);
    }
  }, [loading]);

  const previousSupplierRef = useRef(selectedSupplier);
  const previousLocationRef = useRef(selectedLocation);
  const previousRepairLocationRef = useRef(selectedRepairLocation);

  useEffect(() => {
    if (
      previousSupplierRef.current !== selectedSupplier ||
      previousLocationRef.current !== selectedLocation ||
      previousRepairLocationRef.current !== selectedRepairLocation
    ) {
      if (currentPage !== 1) handleFirstPageButtonClick();
    }
    previousSupplierRef.current = selectedSupplier;
    previousLocationRef.current = selectedLocation;
    previousRepairLocationRef.current = selectedRepairLocation;
  }, [selectedSupplier, selectedLocation, selectedRepairLocation, currentPage, handleFirstPageButtonClick]);

  const handleHeaderClick = (criteria) => {
    if (sortCriteria === criteria) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortCriteria(criteria);
      setSortOrder('asc');
    }
    setHoveredHeader(null);
  };

  const handleHeaderHover = (criteria) => setHoveredHeader(criteria);

  const normaliseStringForSort = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase();
  };

  const sortComparator = (a, b) => {
    let valueA = a[sortCriteria];
    let valueB = b[sortCriteria];

    if (sortCriteria === 'total') {
      valueA = Number(a.total) || 0;
      valueB = Number(b.total) || 0;
    } else if (['invoice_date', 'uploaded_on', 'created_on'].includes(sortCriteria)) {
      const dateA = parseDateForSort(a[sortCriteria]);
      const dateB = parseDateForSort(b[sortCriteria]);
      valueA = dateA ? dateA.getTime() : 0;
      valueB = dateB ? dateB.getTime() : 0;
    } else if (sortCriteria === 'doc_type_id') {
      valueA = normaliseStringForSort(typeMap[a.doc_type_id]);
      valueB = normaliseStringForSort(typeMap[b.doc_type_id]);
    } else if (sortCriteria === 'doc_status_id') {
      valueA = normaliseStringForSort(statusMap[a.doc_status_id]);
      valueB = normaliseStringForSort(statusMap[b.doc_status_id]);
    } else if (sortCriteria === 'job_number') {
      valueA = normaliseStringForSort(a.job_number);
      valueB = normaliseStringForSort(b.job_number);
    } else if (sortCriteria === 'location_id') {
      valueA = normaliseStringForSort(locationMap[a.location_id]);
      valueB = normaliseStringForSort(locationMap[b.location_id]);
    } else if (sortCriteria === 'repair_location_id') {
      valueA = normaliseStringForSort(locationMap[a.repair_location_id]);
      valueB = normaliseStringForSort(locationMap[b.repair_location_id]);
    } else if (sortCriteria === 'supplier_id') {
      valueA = normaliseStringForSort(supplierMap[a.supplier_id]);
      valueB = normaliseStringForSort(supplierMap[b.supplier_id]);
    } else {
      valueA = normaliseStringForSort(valueA);
      valueB = normaliseStringForSort(valueB);
    }

    if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedData = sortCriteria
    ? [...filteredDataList].sort(sortComparator)
    : filteredDataList;

  const hasTableRows = Array.isArray(sortedData) && sortedData.length > 0;
  const isTableLoading = Boolean(loading);
  const isInitialLoading = isTableLoading && !hasFetchedData;
  const showingSummary = totalDocuments
    ? `Showing ${startDocNum}-${endDocNum} of ${totalDocuments}`
    : hasFetchedData
      ? 'No documents to display'
      : 'Showing 0-0 of 0';
  const netTotalDisplay = `Net Total: ${formatCurrency(totalNetValue || 0)}`;

  const allDocStatusesSelected = Array.isArray(docStatuses) && docStatuses.length > 0 && selectedDocStatuses.length === docStatuses.length;
  const someDocStatusesSelected = Array.isArray(docStatuses) && selectedDocStatuses.length > 0 && selectedDocStatuses.length < docStatuses.length;
  const allDocTypesSelected = Array.isArray(docTypes) && docTypes.length > 0 && selectedDocTypes.length === docTypes.length;
  const someDocTypesSelected = Array.isArray(docTypes) && selectedDocTypes.length > 0 && selectedDocTypes.length < docTypes.length;

  const docStatusSelectAllOption = { doc_status_id: '__all__', name: 'Select All Document Statuses' };
  const docTypeSelectAllOption = { doc_type_id: '__all__', name: 'Select All Document Types' };

  const docStatusOptions = Array.isArray(docStatuses) && docStatuses.length > 0 ? [docStatusSelectAllOption, ...docStatuses] : docStatuses || [];
  const docTypeOptions = Array.isArray(docTypes) && docTypes.length > 0 ? [docTypeSelectAllOption, ...docTypes] : docTypes || [];

  const docStatusValue = Array.isArray(docStatuses)
    ? docStatuses.filter((status) => selectedDocStatuses.includes(status.doc_status_id))
    : [];
  const docTypeValue = Array.isArray(docTypes)
    ? docTypes.filter((type) => selectedDocTypes.includes(type.doc_type_id))
    : [];

  const normaliseValue = (value) => (value == null ? '' : String(value).trim());
  const normaliseDate = (value) => (value == null ? '' : value);
  const arraysMatch = (arr = [], defaults = []) => {
    if (!Array.isArray(arr) || !Array.isArray(defaults)) return false;
    if (arr.length !== defaults.length) return false;
    const sortedArr = [...arr].sort();
    const sortedDefaults = [...defaults].sort();
    return sortedArr.every((item, index) => sortedDefaults[index] === item);
  };

  const showClearButton = useMemo(() => {
    const supplierChanged = normaliseValue(selectedSupplier) !== '';
    const locationChanged = normaliseValue(selectedLocation) !== '';
    const repairLocationChanged = normaliseValue(selectedRepairLocation) !== '';
    const supplierStartChanged = normaliseValue(supplierStartChar) !== '';
    const supplierEndChanged = normaliseValue(supplierEndChar) !== '';
    const statusesChanged = !arraysMatch(selectedDocStatuses, defaultSelectedDocStatuses);
    const docTypesChanged = !arraysMatch(selectedDocTypes, defaultSelectedDocTypes);
    const nominalCodeChanged = normaliseValue(selectedNominalCode) !== '';
    const docSearchChanged = normaliseValue(docQuery) !== '' || normaliseValue(docSearchQuery) !== '';
    const jobSearchChanged = normaliseValue(jobQuery) !== '' || normaliseValue(jobNumberFilter) !== '';
    const uploadedChanged = normaliseDate(uploadedStartDate) !== '' || normaliseDate(uploadedEndDate) !== '';
    const createdChanged = normaliseDate(createdStartDate) !== '' || normaliseDate(createdEndDate) !== '';
    const invoiceChanged = normaliseDate(invoiceStartDate) !== '' || normaliseDate(invoiceEndDate) !== '';
    const discountChanged = normaliseValue(discountValue) !== '';
    const includeInNetTotalsChanged = normaliseValue(includeInNetTotalsFilter || 'any') !== 'any';
    const creditRequestedChanged = normaliseValue(creditRequestedFilter || 'any') !== 'any';
    const sortCriteriaChanged = normaliseValue(sortCriteria) !== normaliseValue(defaultSortCriteria);
    const sortOrderChanged = normaliseValue(sortOrder || 'asc') !== normaliseValue(defaultSortOrder || 'asc');

    return (
      supplierChanged ||
      locationChanged ||
      repairLocationChanged ||
      supplierStartChanged ||
      supplierEndChanged ||
      statusesChanged ||
      docTypesChanged ||
      nominalCodeChanged ||
      docSearchChanged ||
      jobSearchChanged ||
      uploadedChanged ||
      createdChanged ||
      invoiceChanged ||
      discountChanged ||
      includeInNetTotalsChanged ||
      creditRequestedChanged ||
      sortCriteriaChanged ||
      sortOrderChanged
    );
  }, [
    selectedSupplier,
    selectedLocation,
    selectedRepairLocation,
    selectedNominalCode,
    selectedDocStatuses,
    selectedDocTypes,
    defaultSelectedDocStatuses,
    defaultSelectedDocTypes,
    docQuery,
    jobQuery,
    docSearchQuery,
    jobNumberFilter,
    supplierStartChar,
    supplierEndChar,
    uploadedStartDate,
    uploadedEndDate,
    createdStartDate,
    createdEndDate,
    invoiceStartDate,
    invoiceEndDate,
    discountValue,
    includeInNetTotalsFilter,
    creditRequestedFilter,
    sortCriteria,
    sortOrder,
    defaultSortCriteria,
    defaultSortOrder,
  ]);

  // NEW: search handler
  const handleDocSearch = (e) => {
    e?.preventDefault();
    // Trigger parent search (filters the dataList in the parent)
    const trimmed = docQuery.trim();
    setDocQuery(trimmed);
    setJobQuery('');
    onJobNumberSearch?.('');
    onSearchDocument?.(trimmed);
  };

  // NEW: clear button should also clear the search box
  const handleClearAll = () => {
    handleClearFilters?.();
    setDocQuery('');
    setJobQuery('');
    onSearchDocument?.(''); // reset to full list in parent
    onUploadedStartDateChange?.('');
    onUploadedEndDateChange?.('');
    onCreatedStartDateChange?.('');
    onCreatedEndDateChange?.('');
    onInvoiceStartDateChange?.('');
    onInvoiceEndDateChange?.('');
    onDiscountOperatorChange?.('<');
    onDiscountValueChange?.('');
    onIncludeInNetTotalsFilterChange?.('true');
    onCreditRequestedFilterChange?.('false');
    onJobNumberSearch?.('');
    handleNominalCodeChange?.('');
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const desiredPage = Number(pageInput);
    if (Number.isNaN(desiredPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(Math.max(desiredPage, 1), totalPages || 1);
    if (clampedPage !== currentPage) {
      handlePageChange(clampedPage);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleUploadedStartChange = (value) => {
    if (value && value.length !== 10) return;
    onUploadedStartDateChange?.(value);
  };

  const handleUploadedEndChange = (value) => {
    if (value && value.length !== 10) return;
    onUploadedEndDateChange?.(value);
  };

  const handleCreatedStartChange = (value) => {
    if (value && value.length !== 10) return;
    onCreatedStartDateChange?.(value);
  };

  const handleCreatedEndChange = (value) => {
    if (value && value.length !== 10) return;
    onCreatedEndDateChange?.(value);
  };

  const handleInvoiceStartChange = (value) => {
    if (value && value.length !== 10) return;
    onInvoiceStartDateChange?.(value);
  };

  const handleInvoiceEndChange = (value) => {
    if (value && value.length !== 10) return;
    onInvoiceEndDateChange?.(value);
  };

  const handleDiscountOperatorChange = (value) => {
    onDiscountOperatorChange?.(value);
  };

  const handleDiscountValueChange = (value) => {
    if (value === '' || value === null || value === undefined) {
      onDiscountValueChange?.('');
      return;
    }
    const normalised = value.toString().replace(/[^0-9.-]/g, '');
    onDiscountValueChange?.(normalised);
  };

  const docTypeEmptyPendingRef = useRef(false);
  const docStatusEmptyPendingRef = useRef(false);

  const handleDocStatusAutocompleteChange = (event, newValue, reason, details) => {
    if (!Array.isArray(docStatuses) || docStatuses.length === 0) return;

    const isSelectAllOption = details?.option?.doc_status_id === '__all__';

    if (reason === 'selectOption' && isSelectAllOption) {
      if (allDocStatusesSelected) {
        handleDocStatusChange([]);
        docStatusEmptyPendingRef.current = true;
      } else {
        handleDocStatusChange(docStatuses.map((status) => status.doc_status_id));
        docStatusEmptyPendingRef.current = false;
      }
      return;
    }

    if (reason === 'clear') {
      handleDocStatusChange([]);
      docStatusEmptyPendingRef.current = true;
      return;
    }

    const filteredValue = newValue.filter((status) => status.doc_status_id !== '__all__');
    handleDocStatusChange(filteredValue.map((status) => status.doc_status_id));
    docStatusEmptyPendingRef.current = filteredValue.length === 0;
  };

  const handleDocTypeAutocompleteChange = (event, newValue, reason, details) => {
    if (!Array.isArray(docTypes) || docTypes.length === 0) return;

    const isSelectAllOption = details?.option?.doc_type_id === '__all__';

    if (reason === 'selectOption' && isSelectAllOption) {
      if (allDocTypesSelected) {
        handleDocTypeChange([]);
        docTypeEmptyPendingRef.current = true;
      } else {
        handleDocTypeChange(docTypes.map((type) => type.doc_type_id));
        docTypeEmptyPendingRef.current = false;
      }
      return;
    }

    if (reason === 'clear') {
      handleDocTypeChange([]);
      docTypeEmptyPendingRef.current = true;
      return;
    }

    const filteredValue = newValue.filter((type) => type.doc_type_id !== '__all__');
    handleDocTypeChange(filteredValue.map((type) => type.doc_type_id));
    docTypeEmptyPendingRef.current = filteredValue.length === 0;
  };

  const handleJobSearch = (event) => {
    event?.preventDefault();
    const trimmed = jobQuery.trim();
    setJobQuery(trimmed);
    onJobNumberSearch?.(trimmed);
    setDocQuery('');
  };

  const normalisedStartChar = (supplierStartChar || '').toUpperCase();
  const endCharOptions = useMemo(
    () =>
      ALPHABET_OPTIONS.filter((letter) => {
        if (!normalisedStartChar) return true;
        return letter >= normalisedStartChar;
      }),
    [normalisedStartChar],
  );

  const showDocumentView = Boolean(documentView);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 1 }}>
      <Grid
        container
        spacing={{ xs: 3, lg: 4 }}
        sx={{ mx: 'auto', width: '100%', maxWidth: 'min(95vw, 1800px)' }}
      >
        <Grid
          item
          xs={12}
          sx={{
            mb: { xs: 4, md: 6 },
            minHeight: { xs: 440, md: 320 },
            display: 'flex',
          }}
        >
          <Grid container spacing={2} columns={{ xs: 12, md: 24 }}>
          <Grid item xs={12} md={12}>
            <Grid container spacing={2} columns={{ xs: 12, md: 12 }}>
              {/* Search by Document Number */}
              <Grid item xs={12} md={6}>
                <Box className="filterElement" component="form" onSubmit={handleDocSearch}>
                  <Typography variant="subtitle1" className="filterTitle">
                    Document Number
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      value={docQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDocQuery(value);
                        if (!value.trim()) {
                          onSearchDocument?.('');
                        }
                      }}
                      placeholder="Enter document number"
                      fullWidth
                      size="small"
                    />
                    <Button
                      variant="contained"
                      onClick={handleDocSearch}
                      type="submit"
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Job Number */}
              <Grid item xs={12} md={6}>
                <Box className="filterElement" component="form" onSubmit={handleJobSearch}>
                  <Typography variant="subtitle1" className="filterTitle">
                    Job Number
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      value={jobQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setJobQuery(value);
                        if (!value.trim()) {
                          onJobNumberSearch?.('');
                        }
                      }}
                      placeholder="Enter job number"
                      fullWidth
                      size="small"
                    />
                    <Button
                      variant="contained"
                      onClick={handleJobSearch}
                      type="submit"
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={8} sx={{ mt: { md: 2 } }}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Supplier
                  </Typography>
                  <Autocomplete
                    options={suppliers}
                    value={selectedSupplierOption}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const id = option.supplier_id ?? '';
                      const name = option.name ?? '';
                      return id ? `${id} - ${name}` : name;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span>{option.supplier_id ?? ''}</span>
                        <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                      </li>
                    )}
                    onChange={(event, newValue) =>
                      handleSupplierChange({ target: { value: newValue ? newValue.supplier_id : '' } })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="All Suppliers"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {selectedSupplierOption?.discount !== null &&
                              selectedSupplierOption?.discount !== undefined ? (
                                <Box
                                  sx={{
                                    mr: 1,
                                    color: 'text.secondary',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatPercent(selectedSupplierOption.discount)}
                                </Box>
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{ mt: { md: 2 } }}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Discount
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      select
                      value={discountOperator || '<'}
                      onChange={(event) => handleDiscountOperatorChange(event.target.value)}
                      size="small"
                      sx={{ minWidth: 90 }}
                    >
                      <MenuItem value="<=">{'<='}</MenuItem>
                      <MenuItem value="<">{'<'}</MenuItem>
                      <MenuItem value=">">{'>'}</MenuItem>
                      <MenuItem value=">=">{'>='}</MenuItem>
                    </TextField>
                    <TextField
                      value={discountValue ?? ''}
                      onChange={(event) => handleDiscountValueChange(event.target.value)}
                      placeholder="e.g. 10.00"
                      fullWidth
                      size="small"
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={12}>
            <Grid container spacing={2} columns={{ xs: 12, md: 12 }}>
              {/* Location */}
              <Grid item xs={12} md={6}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Location
                  </Typography>
                  <Autocomplete
                    options={locations}
                    value={
                      selectedLocation
                        ? locations.find((l) => l.location_id === selectedLocation)
                        : null
                    }
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const id = option.location_id ?? '';
                      const name = option.name ?? '';
                      return id ? `${id} - ${name}` : name;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span>{option.location_id ?? ''}</span>
                        <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                      </li>
                    )}
                    onChange={(event, newValue) =>
                      handleLocationChange?.({ target: { value: newValue ? newValue.location_id : '' } })
                    }
                    renderInput={(params) => <TextField {...params} placeholder="All Locations" fullWidth />}
                  />
                </Box>
              </Grid>

              {/* Repair Location */}
              <Grid item xs={12} md={6}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Repair Location
                  </Typography>
                  <Autocomplete
                    options={locations}
                    value={
                      selectedRepairLocation
                        ? locations.find((l) => l.location_id === selectedRepairLocation)
                        : null
                    }
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const id = option.location_id ?? '';
                      const name = option.name ?? '';
                      return id ? `${id} - ${name}` : name;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span>{option.location_id ?? ''}</span>
                        <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                      </li>
                    )}
                    onChange={(event, newValue) =>
                      handleRepairLocationChange?.({ target: { value: newValue ? newValue.location_id : '' } })
                    }
                    renderInput={(params) => <TextField {...params} placeholder="All Repair Locations" fullWidth />}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Supplier Start Letter
                  </Typography>
                  <TextField
                    select
                    value={supplierStartChar || ''}
                    onChange={(event) => onSupplierStartCharChange?.(event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Any (defaults to A)</MenuItem>
                    {ALPHABET_OPTIONS.map((letter) => (
                      <MenuItem key={letter} value={letter}>
                        {letter}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Supplier End Letter
                  </Typography>
                  <TextField
                    select
                    value={supplierEndChar || ''}
                    onChange={(event) => onSupplierEndCharChange?.(event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Any (defaults to Z)</MenuItem>
                    {endCharOptions.map((letter) => (
                      <MenuItem key={letter} value={letter}>
                        {letter}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Include In Net Totals
                  </Typography>
                  <TextField
                    select
                    value={includeInNetTotalsFilter || 'any'}
                    onChange={(event) => onIncludeInNetTotalsFilterChange?.(event.target.value)}
                    fullWidth
                    size="small"
                  >
                    {BOOLEAN_FILTER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className="filterElement">
                  <Typography variant="subtitle1" className="filterTitle">
                    Credit Requested
                  </Typography>
                  <TextField
                    select
                    value={creditRequestedFilter || 'any'}
                    onChange={(event) => onCreditRequestedFilterChange?.(event.target.value)}
                    fullWidth
                    size="small"
                  >
                    {BOOLEAN_FILTER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Document Type */}
          <Grid item xs={12}>
            <Box className="filterElement">
              <Typography variant="subtitle1" className="filterTitle">
                Document Type
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={docTypeOptions}
                value={docTypeValue}
                getOptionLabel={(option) => option.name || option.doc_type_id || ''}
                onChange={handleDocTypeAutocompleteChange}
                onBlur={() => {
                  if (docTypeEmptyPendingRef.current && Array.isArray(docTypes) && docTypes.length > 0) {
                    docTypeEmptyPendingRef.current = false;
                    handleDocTypeChange(docTypes.map((type) => type.doc_type_id));
                  }
                }}
                
                isOptionEqualToValue={(option, value) => option.doc_type_id === value?.doc_type_id}
                renderOption={(props, option) => {
                  const isSelectAll = option.doc_type_id === '__all__';
                  const checked = isSelectAll ? allDocTypesSelected : selectedDocTypes.includes(option.doc_type_id);
                  const indeterminate = isSelectAll ? someDocTypesSelected : false;
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={option.name}
                        secondary={!isSelectAll ? option.doc_type_id : undefined}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => <TextField {...params} placeholder="All Types" fullWidth />}
              />
            </Box>
          </Grid>

          {/* Document Status */}
          <Grid item xs={12}>
            <Box className="filterElement">
              <Typography variant="subtitle1" className="filterTitle">
                Document Status
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={docStatusOptions}
                value={docStatusValue}
                getOptionLabel={(option) => option.name || option.doc_status_id || ''}
                onChange={handleDocStatusAutocompleteChange}
                onBlur={() => {
                  if (docStatusEmptyPendingRef.current && Array.isArray(docStatuses) && docStatuses.length > 0) {
                    docStatusEmptyPendingRef.current = false;
                    handleDocStatusChange(docStatuses.map((status) => status.doc_status_id));
                  }
                }}
                sx={multiSelectAutocompleteSx}
                isOptionEqualToValue={(option, value) => option.doc_status_id === value?.doc_status_id}
                renderOption={(props, option) => {
                  const isSelectAll = option.doc_status_id === '__all__';
                  const checked = isSelectAll ? allDocStatusesSelected : selectedDocStatuses.includes(option.doc_status_id);
                  const indeterminate = isSelectAll ? someDocStatusesSelected : false;
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={option.name}
                        secondary={!isSelectAll ? option.doc_status_id : undefined}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => <TextField {...params} placeholder="All Statuses" fullWidth />}
              />
            </Box>
          </Grid>

          {/* Nominal Code */}
          <Grid item xs={12}>
            <Box className="filterElement">
              <Typography variant="subtitle1" className="filterTitle">
                Nominal Code
              </Typography>
              <Autocomplete
                options={nominalCodeOptions}
                value={nominalCodeOptions.find((option) => option.nominal_code_id === selectedNominalCode) || null}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const code = option.nominal_code_id || '';
                  const name = option.name || '';
                  return name ? `${code} - ${name}` : code;
                }}
                onChange={(event, newValue) => {
                  if (typeof handleNominalCodeChange === 'function') {
                    handleNominalCodeChange(newValue ? newValue.nominal_code_id : '');
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="All Nominal Codes" fullWidth />
                )}
                isOptionEqualToValue={(option, value) => option.nominal_code_id === value?.nominal_code_id}
                clearOnEscape
              />
            </Box>
          </Grid>

          {/* Uploaded On */}
          <Grid item xs={12} md={12}>
            <Box className="filterElement">
              <Typography variant="subtitle1" className="filterTitle">
                Uploaded On
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={uploadedStartDate || ''}
                  onChange={(e) => handleUploadedStartChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={uploadedEndDate || ''}
                  onChange={(e) => handleUploadedEndChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          {/* Document Date */}
          <Grid item xs={12} md={12}>
            <Box className="filterElement">
              <Typography variant="subtitle1" className="filterTitle">
                Document Date
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={invoiceStartDate || ''}
                  onChange={(e) => handleInvoiceStartChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={invoiceEndDate || ''}
                  onChange={(e) => handleInvoiceEndChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          {/* Created On */}
          <Grid item xs={12} md={12}>
            <Box className="filterElement">
              <Typography variant="subtitle1" className="filterTitle">
                Created On
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={createdStartDate || ''}
                  onChange={(e) => handleCreatedStartChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={createdEndDate || ''}
                  onChange={(e) => handleCreatedEndChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={24}>
            <Box className="clearButton" sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 1, md: 2 } }}>
              {showClearButton && (
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearAll}
                  size="small"
                  sx={{ minWidth: 0, px: 1.5 }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>
      </Grid>

        </Grid>

        <Grid item xs={12}>
          <Box sx={{ position: 'relative', mt: 3, pb: showDocumentView ? 0 : 6 }}>
            {showDocumentView ? (
              <Box sx={{ width: '100%' }}>{documentView}</Box>
            ) : (
              <>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={() => onExportCsv?.()}
              disabled={exporting || loading}
              size="small"
              sx={{ position: 'absolute', top: -40, right: 0, zIndex: 2 }}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <TableContainer component={Paper} sx={{ width: '100%', position: 'relative' }}>
              {isTableLoading && hasTableRows && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.65)',
                    zIndex: 2,
                  }}
                >
                  <CircularProgress size={24} thickness={4} />
                </Box>
              )}
              <Table sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'uploaded_on' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('uploaded_on')}
                      onMouseEnter={() => handleHeaderHover('uploaded_on')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Uploaded On</strong>
                        {(sortCriteria === 'uploaded_on' || hoveredHeader === 'uploaded_on') &&
                          (sortCriteria === 'uploaded_on'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'invoice_date' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('invoice_date')}
                      onMouseEnter={() => handleHeaderHover('invoice_date')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Document Date</strong>
                        {(sortCriteria === 'invoice_date' || hoveredHeader === 'invoice_date') &&
                          (sortCriteria === 'invoice_date'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'job_number' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('job_number')}
                      onMouseEnter={() => handleHeaderHover('job_number')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Job Number</strong>
                        {(sortCriteria === 'job_number' || hoveredHeader === 'job_number') &&
                          (sortCriteria === 'job_number'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'invoice_number' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('invoice_number')}
                      onMouseEnter={() => handleHeaderHover('invoice_number')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Document Number</strong>
                        {(sortCriteria === 'invoice_number' || hoveredHeader === 'invoice_number') &&
                          (sortCriteria === 'invoice_number'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>
                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'location_id' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('location_id')}
                      onMouseEnter={() => handleHeaderHover('location_id')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Location</strong>
                        {(sortCriteria === 'location_id' || hoveredHeader === 'location_id') &&
                          (sortCriteria === 'location_id'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>
                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'repair_location_id' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('repair_location_id')}
                      onMouseEnter={() => handleHeaderHover('repair_location_id')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Repair Location</strong>
                        {(sortCriteria === 'repair_location_id' || hoveredHeader === 'repair_location_id') &&
                          (sortCriteria === 'repair_location_id'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>
                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'supplier_id' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('supplier_id')}
                      onMouseEnter={() => handleHeaderHover('supplier_id')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Supplier</strong>
                        {(sortCriteria === 'supplier_id' || hoveredHeader === 'supplier_id') &&
                          (sortCriteria === 'supplier_id'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'total' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('total')}
                      onMouseEnter={() => handleHeaderHover('total')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Total</strong>
                        {(sortCriteria === 'total' || hoveredHeader === 'total') &&
                          (sortCriteria === 'total'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'discount' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('discount')}
                      onMouseEnter={() => handleHeaderHover('discount')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Discount (%)</strong>
                        {(sortCriteria === 'discount' || hoveredHeader === 'discount') &&
                          (sortCriteria === 'discount'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'doc_type_id' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('doc_type_id')}
                      onMouseEnter={() => handleHeaderHover('doc_type_id')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Type</strong>
                        {(sortCriteria === 'doc_type_id' || hoveredHeader === 'doc_type_id') &&
                          (sortCriteria === 'doc_type_id'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'doc_status_id' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('doc_status_id')}
                      onMouseEnter={() => handleHeaderHover('doc_status_id')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Status</strong>
                        {(sortCriteria === 'doc_status_id' || hoveredHeader === 'doc_status_id') &&
                          (sortCriteria === 'doc_status_id'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'created_on' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('created_on')}
                      onMouseEnter={() => handleHeaderHover('created_on')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Created On</strong>
                        {(sortCriteria === 'created_on' || hoveredHeader === 'created_on') &&
                          (sortCriteria === 'created_on'
                            ? (sortOrder === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />)
                            : <ArrowDownward className="hoverIcon" />)}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {hasTableRows ? (
                    sortedData.map((data, index) => (
                      <TableRow
                        key={index}
                        onClick={() => handleCardClick(
                          data.id,
                          {
                            globalIndex:
                              typeof data.__sequenceIndex === 'number'
                                ? data.__sequenceIndex
                                : startDocNum + index - 1,
                          }
                        )}
                        className="tableRow"
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(data.uploaded_on)}>{formatCellDisplay(data.uploaded_on)}</TableCell>
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(data.invoice_date)}>{formatCellDisplay(data.invoice_date)}</TableCell>
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(data.job_number)}>{formatCellDisplay(data.job_number)}</TableCell>
                        <TableCell sx={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(data.invoice_number)}>{formatCellDisplay(data.invoice_number)}</TableCell>
                        <TableCell sx={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(locationMap[data.location_id])}>{formatCellDisplay(locationMap[data.location_id])}</TableCell>
                        <TableCell sx={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(locationMap[data.repair_location_id])}>{formatCellDisplay(locationMap[data.repair_location_id])}</TableCell>
                        <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(supplierMap[data.supplier_id])}>{formatCellDisplay(supplierMap[data.supplier_id])}</TableCell>
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCurrency(data.total)}>{formatCurrency(data.total)}</TableCell>
                        <TableCell sx={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatPercent(data.discount)}>{formatPercent(data.discount)}</TableCell>
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(typeMap[data.doc_type_id])}>{formatCellDisplay(typeMap[data.doc_type_id])}</TableCell>
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(statusMap[data.doc_status_id])}>{formatCellDisplay(statusMap[data.doc_status_id])}</TableCell>
                        <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatCellDisplay(data.created_on)}>{formatCellDisplay(data.created_on)}</TableCell>
                      </TableRow>
                    ))
                  ) : isTableLoading ? (
                    <TableRow className="tableRow">
                      <TableCell colSpan={12} align="center" sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                          <CircularProgress size={18} />
                          <Typography color="text.secondary">{isInitialLoading ? 'Loading...' : showingSummary}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow className="tableRow">
                      <TableCell colSpan={12} align="center">
                        No record(s) found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            padding: '0px 16px'
                          }}
                        >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {netTotalDisplay}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                          }}
                        >
                        

                        <TextField
                          value={pageInput}
                          onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                          onKeyDown={handlePageInputKeyDown}
                          label="Page"
                          size="small"
                          sx={{ width: 90 }}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          disabled={isTableLoading || !totalDocuments}
                        />

                        <Typography variant="body2">
                          {totalDocuments ? `of ${totalPages}` : ''}
                        </Typography>
                        <Typography variant="body2">{showingSummary}</Typography>

                        <TablePagination
                          component="div"
                          count={totalDocuments || 0}
                          page={Math.max(0, (currentPage || 1) - 1)}
                          onPageChange={(_, p) => handlePageChange(p + 1)}
                          rowsPerPage={pageLimit}
                          rowsPerPageOptions={[pageLimit]}
                          labelRowsPerPage=""
                          labelDisplayedRows={() => ''}
                          ActionsComponent={(paginationProps) => (
                            <GridPaginationActions
                              page={paginationProps.page}
                              count={paginationProps.count}
                              rowsPerPage={paginationProps.rowsPerPage}
                              onPageChange={paginationProps.onPageChange}
                              disabled={isTableLoading || !totalDocuments}
                            />
                          )}
                          sx={{
                            '.MuiTablePagination-toolbar': { p: 0 },
                            '.MuiTablePagination-selectLabel': { display: 'none' },
                            '.MuiTablePagination-displayedRows': { m: 0 },
                          }}
                        />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GridView;
